import { getUserTabs } from 'components/Header/header-constant'
import { useAuthStore } from 'context/AuthStore'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { filterInitialState, paginationInitialState } from 'lib/constants/initial-states'
import { ACCOUNTANT, DICT_ADMIN, HEAD_UNDERWRITER, MANAGER, SUPER_UNDERWRITER, UNDERWRITER } from 'lib/constants/roles'
import { useResErrors } from 'lib/hooks'
import { getFullName } from 'lib/utils'
import { debounce } from 'lodash'
import { enqueueSnackbar } from 'notistack'
import { createContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

export const HomeStoreContext = createContext({
  homeStore: {},
  homeStoreFunc: {}
})

const getSavedState = (key, initialState) => {
  const savedState = sessionStorage.getItem(key)
  return savedState ? JSON.parse(savedState) : initialState
}

const useSessionStorageState = (key, initialState) => {
  const [state, setState] = useState(() => getSavedState(key, initialState))

  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(state))
  }, [key, state])

  return [state, setState]
}

const HomeStoreProvider = props => {
  const { standardCheck } = useResErrors()
  const navigate = useNavigate()
  const { authStore, authStoreFunc } = useAuthStore()
  const [listData, setListData] = useState([])
  const [activeApplication, setActiveApplication] = useState(null)
  const [banks, setBanks] = useState([])
  const [creditProducts, setCreditProducts] = useState([])

  const [tab, setTab] = useSessionStorageState('tab', getUserTabs(authStore.isUserRole)?.[0]?.code || '')
  const [filter, setFilter] = useSessionStorageState('filter', filterInitialState)
  const [pagination, setPagination] = useSessionStorageState('pagination', paginationInitialState)

  const [underwriterStatistics, setUnderwriterStatistics] = useState(null)
  const [credAdminStatistic, setCredAdminStatistic] = useState(null)

  const [countFiltered, setCountFiltered] = useState(0)

  const [adminModal, setAdminModal] = useState({
    open: false,
    mode: 'view',
    tab: '',
    data: null
  })

  const openAdminModal = (mode, tab, data) => {
    setAdminModal({ open: true, mode, tab, data })
  }

  const closeAdminModal = () => {
    setAdminModal(prev => ({ ...prev, open: false }))
  }

  const getFormattedFilter = () => {
    const filterParams = {}

    for (const [key, value] of Object.entries(filter)) {
      if (value) {
        filterParams[key] = value
      }
    }

    return filterParams
  }

  const assignByRole = async () => {
    try {
      authStoreFunc.setLoading(true)

      const data = {
        assignTo: authStore?.user?.data?.username,
        assignToFullName: getFullName(authStore?.user?.data?.firstName, authStore?.user?.data?.lastName),
        role: authStore.isUserRole
      }

      const response = await CreditFlow.assignByRole(data)

      standardCheck(response, () => {
        if (response.data) {
          // if ([UNDERWRITER, HEAD_UNDERWRITER, SUPER_UNDERWRITER].includes(authStore.isUserRole)) {
          navigate(`/application/${response.data?.id}`)
          // }
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
      authStoreFunc.setLoading(false)
    }
  }

  const getActiveByToken = async () => {
    try {
      const response = await CreditFlow.getActiveByToken()

      if (response.data) {
        setActiveApplication(response.data)
      }
    } catch (error) {
      console.error(error)
    } finally {
    }
  }

  const getBanks = async () => {
    try {
      const response = await CreditFlow.getBanks()
      if (response.data) {
        setBanks(response.data?.list)
      }
    } catch (error) {
      console.error(error)
    } finally {
    }
  }

  const getCreditProducts = async (name = '') => {
    try {
      const data = {
        name,
        limit: 1000,
        offset: 0
      }
      const response = await CreditFlow.getCreditProducts(data)
      standardCheck(response, () => {
        if (response.data) {
          setCreditProducts(response.data.list)
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
    }
  }

  const getUnderwriterStatistics = async () => {
    try {
      const response = await CreditFlow.getUnderwriterStatistics()
      if (response.data) {
        setUnderwriterStatistics(response.data)
      }
    } catch (error) {
      console.error(error)
    } finally {
    }
  }

  const getCredAdminStatistic = async () => {
    try {
      const response = await CreditFlow.getCredAdminStatistic()
      if (response.data) {
        setCredAdminStatistic(response.data)
      }
    } catch (error) {
      console.error(error)
    } finally {
    }
  }

  const fetchDataList = async (fetchFunc, data, limit = 30, offset = 0) => {
    const requestData = {
      ...data,
      limit,
      offset: Math.max((offset - 1) * limit, 0)
    }
    const response = await fetchFunc(requestData)
    if (response?.data?.list) {
      const { list, countFiltered } = response.data
      return { list, count: countFiltered }
    }

    return { list: [], count: 0 }
  }

  const getApplicationsList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getApplicationsList, data, limit, offset)
  }

  const getHeadUnderwriterList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getHeadUnderwriterList, data, limit, offset)
  }

  const getClientsList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getClientsList, data, limit, offset)
  }

  const getCollateralsList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getCollateralsList, data, limit, offset)
  }

  const getContractList = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getContractList, data, limit, offset)
  }
  const getDealerGroups = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getDealerGroupsFilter, data, limit, offset)
  }
  const getDealerCenters = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getDealerCentersFilter, data, limit, offset)
  }
  const getCitiesFilter = (data, limit, offset) => {
    return fetchDataList(CreditFlow.getCitiesFilter, data, limit, offset)
  }
  const getOwnApllicationList = (data, limit, offset) => {
    const requestData = {
      ...data,
      interactedUser: authStore.user.data.username,
      limit,
      offset: Math.max((offset - 1) * 10, 0)
    }
    return fetchDataList(CreditFlow.getApplicationsList, requestData)
  }

  const getData = async (data, limit, offset) => {
    const role = authStore.isUserRole
    const assignTo = authStore?.user?.data?.username

    const fetchData = {
      [DICT_ADMIN]: {
        groups: () => getDealerGroups({ ...data }, limit, offset),
        dealers: () => getDealerCenters({ ...data }, limit, offset),
        locations: () => getCitiesFilter({ ...data }, limit, offset)
      },
      [UNDERWRITER]: {
        main: () => getApplicationsList({ ...data, assignTo }, limit, offset),
        applications: () => getApplicationsList({ ...data, assignTo }, limit, offset),
        clients: () => getClientsList(data, limit, offset),
        contracts: () => getContractList({ ...data, assignTo }, limit, offset),
        collaterals: () => getCollateralsList(data, limit, offset),
        ownApplications: () => getOwnApllicationList(data, limit, offset)
      },
      [HEAD_UNDERWRITER]: {
        main: () => getHeadUnderwriterList(data, limit, offset),
        applications: () => getApplicationsList({ ...data, assignTo }, limit, offset),
        clients: () => getClientsList(data, limit, offset),
        contracts: () => getContractList({ ...data, assignTo }, limit, offset),
        collaterals: () => getCollateralsList(data, limit, offset),
        ownApplications: () => getOwnApllicationList(data, limit, offset)
      },
      [SUPER_UNDERWRITER]: {
        main: () => getHeadUnderwriterList(data, limit, offset),
        applications: () => getApplicationsList({ ...data, assignTo }, limit, offset),
        clients: () => getClientsList(data, limit, offset),
        contracts: () => getContractList({ ...data, assignTo }, limit, offset),
        collaterals: () => getCollateralsList(data, limit, offset),
        ownApplications: () => getOwnApllicationList(data, limit, offset)
      },
      [MANAGER]: {
        applications: () => getApplicationsList({ ...data, assignTo }, limit, offset),
        clients: () => getClientsList(data, limit, offset),
        contracts: () => getContractList({ ...data, assignTo }, limit, offset),
        collaterals: () => getCollateralsList(data, limit, offset)
      }
    }

    if (role === ACCOUNTANT) {
      const { list, count } = await getContractList(data, limit, offset)
      setListData(list)
      setPagination(prevState => ({ ...prevState, count }))
      return
    }

    const fetchFunction = fetchData[role]?.[tab]

    if (fetchFunction) {
      const { list, count } = await fetchFunction()
      setListData(list)
      setPagination(prevState => ({ ...prevState, count }))
    }
  }

  const clearData = () => {
    setFilter({ ...filterInitialState })
    setPagination({ ...paginationInitialState })
  }

  const handleChangeTabs = code => {
    setTab(code)
    clearData()
  }

  const handleClearFilter = () => {
    getData()
    clearData()
  }

  const handleFilterChange = e => {
    const name = e.target.name
    const value = e.target.value

    setFilter(prevState => ({ ...prevState, [name]: value }))
  }

  const handleSearch = debounce(async () => {
    const filter = getFormattedFilter()
    if (filter.fullName && filter.fullName?.length < 5) {
      enqueueSnackbar(`Поле "ФИО клиента" должно содержать минимум 5 символов`, { variant: 'error' })
      return
    }
    getData(getFormattedFilter(), pagination.limit, 0)
  }, 300)

  const handlePaginationChange = async (e, val) => {
    getData(getFormattedFilter(), pagination.limit, val)

    setPagination(prevState => ({
      ...prevState,
      offset: val - 1
    }))
  }

  const createDictionaryItem = async (tab, data) => {
    let payload
    switch (tab) {
      case 'groups':
        payload = { name: data.name, code: data.code }
        break
      case 'dealers':
        payload = {
          name: data.name,
          code: data.code,
          cityId: data.cityId ? Number(data.cityId) : null,
          dealerGroupCode: data.dealerGroupCode ? data.dealerGroupCode : null
        }
        break
      case 'locations':
        payload = { name: data.name }
        break
      default:
        payload = data
    }

    const methodMap = {
      groups: CreditFlow.createDealerGroup,
      dealers: CreditFlow.createDealerCenter,
      locations: CreditFlow.createCity
    }

    const response = await methodMap[tab]?.(payload)
    standardCheck(response, () => {
      enqueueSnackbar('Успешно создано', { variant: 'success' })
      getData(getFormattedFilter(), pagination.limit, pagination.offset)
    })
  }

  const updateDictionaryItem = async (tab, id, data) => {
    let payload
    switch (tab) {
      case 'groups':
        payload = { name: data.name, code: data.code }
        break
      case 'dealers':
        payload = {
          name: data.name,
          code: data.code,
          cityId: data.cityId ? Number(data.cityId) : null,
          dealerGroupCode: data.dealerGroupCode ? data.dealerGroupCode : null
        }
        break
      case 'locations':
        payload = { name: data.name }
        break
      default:
        payload = data
    }

    const methodMap = {
      groups: CreditFlow.updateDealerGroup,
      dealers: CreditFlow.updateDealerCenter,
      locations: CreditFlow.updateCity
    }

    const response = await methodMap[tab]?.(id, payload)
    standardCheck(response, () => {
      enqueueSnackbar('Успешно обновлено', { variant: 'success' })
      getData(getFormattedFilter(), pagination.limit, pagination.offset)
    })
  }

  const deleteDictionaryItem = async (tab, id) => {
    const methodMap = {
      groups: CreditFlow.deleteDealerGroup,
      dealers: CreditFlow.deleteDealerCenter,
      locations: CreditFlow.deleteCity
    }
    const response = await methodMap[tab]?.(id)
    standardCheck(response, () => {
      enqueueSnackbar('Успешно создано', { variant: 'success' })
      getData(getFormattedFilter(), pagination.limit, pagination.offset)
    })
  }

  useEffect(() => {
    getData(getFormattedFilter())
  }, [tab])

  useEffect(() => {
    if ([ACCOUNTANT, UNDERWRITER, HEAD_UNDERWRITER, SUPER_UNDERWRITER].includes(authStore.isUserRole)) {
      getActiveByToken()
    }

    if ([ACCOUNTANT].includes(authStore.isUserRole)) {
      getBanks()
    }

    getCreditProducts()
  }, [])

  const homeStore = {
    filter,
    listData,
    tab,
    pagination,
    activeApplication,
    underwriterStatistics,
    banks,
    creditProducts,
    credAdminStatistic,
    countFiltered,
    adminModal
  }

  const homeStoreFunc = {
    setTab,
    handleChangeTabs,
    handleFilterChange,
    handleSearch,
    handleClearFilter,
    handlePaginationChange,
    getApplicationsList,
    assignByRole,
    getActiveByToken,
    getUnderwriterStatistics,
    getCredAdminStatistic,
    getContractList,
    setCountFiltered,
    openAdminModal,
    closeAdminModal,
    createDictionaryItem,
    updateDictionaryItem,
    deleteDictionaryItem
  }

  return <HomeStoreContext.Provider value={{ homeStore, homeStoreFunc }}>{props.children}</HomeStoreContext.Provider>
}
export default HomeStoreProvider
